import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DEV_URL, rget } from "../../utils/requests";
import Table from "../../components/Table/index";
import Pagination from "../../components/Table/Pagination";
import PageTitle from "../../components/PageTitle/index";
import ViewLineItemModal from "./ViewLineItemModal";
import { useApiFilters } from "../../hooks/useApiFilters";
import { getTablePaginationSizes } from "../../utils/get-table-pagination-sizes";
import { ColumnDef } from "@tanstack/table-core";
import ThreeDotMenu from "../../components/ThreeDotMenu";
import { Dropdown } from "react-bootstrap";

interface IHUTranLineItem {
  id: number;
  huTranID: number;
  accountNo: string;
  utilityName: string;
  enerexUtilityID: string;
  commodityID: number;
  commodityName: string;
  stateID: number;
  status: string;
  huType: string;
  message: string | null;
  usageFrom: string;
  usageUpTo: string;
  usage: number;
  uom: string;
  billCycle: string;
  meterDemandKW: number;
  meterDemandKVA: number;
  powerFactor: number;
  utilityCharges: number | null;
  customerName: string;
}

const HURequestDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [lineItems, setLineItems] = useState<IHUTranLineItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLineItem, setSelectedLineItem] = useState<IHUTranLineItem | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const huTranID = Number(id);

  const { limit, page, setPage, setLimit, orderBy, ascendingOrder, hook } = useApiFilters<IHUTranLineItem>({ page: 0, limit: 25 });
  const tableHook = useCallback(() => ({ ...hook(), extra: null }), [hook]);

  const displayData = [...lineItems]
    .sort((a: any, b: any) => {
      if (!orderBy) return 0;
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === bValue) return 0;
      if (aValue == null) return ascendingOrder ? -1 : 1;
      if (bValue == null) return ascendingOrder ? 1 : -1;

      const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });
      return ascendingOrder ? comparison : -comparison;
    })
    .slice(page * limit, (page + 1) * limit);

  const handleViewLineItem = (lineItem: IHUTranLineItem) => {
    setSelectedLineItem(lineItem);
    setShowViewModal(true);
  };

  const getHURequestDetail = async () => {
    try {
      setLoading(true);

      const response = await rget(
        `${DEV_URL}/api/HistoricalUsage/hu-requests/${huTranID}`
      );

      setLineItems(response?.data?.lineItems || []);
    } catch (error) {
      console.error("Error fetching HU request detail:", error);
      setLineItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (huTranID) {
      getHURequestDetail();
    }
  }, [huTranID]);

  const columns: ColumnDef<IHUTranLineItem>[] = [
    {
      header: "Account No",
      accessorKey: "accountNo",
      cell: (props: any) => {
        const isElectric = props.row.original.commodityName?.toLowerCase() === "electric";
        const icon = isElectric
          ? "mdi mdi-lightning-bolt text-warning"
          : "mdi mdi-fire text-success";
        return (
          <a
            href="#"
            className="text-body"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setSelectedLineItem(props.row.original);
              setShowViewModal(true);
            }}
          >
            <i className={`${icon} me-1`} />
            {props.row.original.accountNo}
          </a>
        );
      },
    },
    {
      header: "Utility Name",
      accessorKey: "utilityName",
      cell: ({ getValue }: any) => getValue() || "-",
    },
    { header: "Customer Name", 
      accessorKey: "customerName" ,
      cell: ({ getValue }: any) => getValue() || "-",
    },
    {
      header: "Status",
      accessorKey: "status",
    },
    {
      header: "Message",
      accessorKey: "message",
      cell: ({ getValue }: any) => getValue() || "-",
    },
    {
      header: "Action",
      id: "actions",
      cell: ({ row }: any) => (
        <ThreeDotMenu>
          <Dropdown.Item onClick={() => handleViewLineItem(row.original)}>
            View
          </Dropdown.Item>
        </ThreeDotMenu>
      ),
      enableSorting: false,
      enableHiding: false,
    },
  ];

  return (
    <>

      <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
        <PageTitle title="HU Accounts" />
        <div className="d-flex gap-2">
          <Button
            variant="link"
            size="sm"
            className="d-flex align-items-center gap-1 p-0 text-decoration-none"
            onClick={() => navigate("/account/hu-request")}
          >
            <i className="mdi mdi-chevron-left fs-5" />
            HU Requests
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              

              <Table<IHUTranLineItem>
                columns={columns}
                data={displayData}
                loading={loading}
                total={lineItems.length}
                hook={tableHook}
                enableSearch={false}
                enablePagination={false}
                enableHideColumns={false}
                enableSorting={true}
                enableFilters={false}
              />
              <Pagination
                limit={limit}
                page={page}
                setPage={setPage}
                setLimit={setLimit}
                total={lineItems.length}
                sizePerPageList={getTablePaginationSizes(lineItems.length, true, 25, 50, 100)}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <ViewLineItemModal
        show={showViewModal}
        lineItem={selectedLineItem}
        onClose={() => {
          setShowViewModal(false);
          setSelectedLineItem(null);
        }}
      />
    </>
  );
};

export default HURequestDetail;
